import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Card, EmptyState, ErrorState, PageHeader, Pill, Skeleton } from "../components/ui";
import { fetchAiInsightResult, fetchAiInsightStatus } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import type { AIInsightRequest } from "../types";

type AnyObject = Record<string, unknown>;

interface InsightAlternative {
  id: string;
  current: string;
  swap: string;
  reason: string | null;
  tier: string | null;
}

interface InsightResultView {
  status: string | null;
  overallAssessment: string | null;
  patterns: string[];
  gaps: string[];
  shoppingList: string[];
  quickWins: string[];
  assumptions: string[];
  alternatives: InsightAlternative[];
  generatedAt: string | null;
  version: string | null;
}

function displayDate(value?: string): string {
  if (!value) {
    return "-";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed);
}

function asObject(value: unknown): AnyObject | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as AnyObject;
}

function toStringValue(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    const single = toStringValue(value);
    return single ? [single] : [];
  }
  const values: string[] = [];
  for (const entry of value) {
    const direct = toStringValue(entry);
    if (direct) {
      values.push(direct);
      continue;
    }
    const object = asObject(entry);
    if (!object) {
      continue;
    }
    const inferred =
      toStringValue(object.label) ??
      toStringValue(object.title) ??
      toStringValue(object.name) ??
      toStringValue(object.reason) ??
      toStringValue(object.note);
    if (inferred) {
      values.push(inferred);
    }
  }
  return values;
}

function pickFirstString(object: AnyObject | null, keys: string[]): string | null {
  if (!object) {
    return null;
  }
  for (const key of keys) {
    const value = toStringValue(object[key]);
    if (value) {
      return value;
    }
  }
  return null;
}

function extractAlternatives(value: unknown): InsightAlternative[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const alternatives: InsightAlternative[] = [];
  for (const [index, entry] of value.entries()) {
    const row = asObject(entry);
    if (!row) {
      continue;
    }

    const directSwap =
      pickFirstString(row, ["replacement", "alternative", "swap", "suggested", "suggested_item", "to"]) ?? null;

    const tier = pickFirstString(row, ["tier"]);
    let suggestedName: string | null = null;
    let suggestedReason: string | null = null;

    const nestedAlternatives = row.alternatives;
    if (Array.isArray(nestedAlternatives) && nestedAlternatives.length > 0) {
      const firstAlt = asObject(nestedAlternatives[0]);
      if (firstAlt) {
        const firstSuggestion = Array.isArray(firstAlt.suggestions) ? asObject(firstAlt.suggestions[0]) : null;
        suggestedName = pickFirstString(firstSuggestion, ["name", "title", "item"]);
        suggestedReason = pickFirstString(firstSuggestion, ["why", "reason", "note"]);
      }
    }

    const current =
      pickFirstString(row, ["item_name", "item", "current_item", "current", "from", "name", "source_item"]) ??
      `Item ${index + 1}`;
    const swap = suggestedName ?? directSwap;
    if (!swap) {
      continue;
    }

    const reason = suggestedReason ?? pickFirstString(row, ["reason", "rationale", "note"]);
    alternatives.push({
      id: pickFirstString(row, ["id", "item_id", "itemId"]) ?? `${current}-${swap}-${index}`,
      current,
      swap,
      reason,
      tier,
    });
  }
  return alternatives;
}

function extractInsightView(payload: unknown): InsightResultView {
  const root = asObject(payload);
  const resultRoot = asObject(root?.result) ?? root;
  const pantrySummary = asObject(resultRoot?.pantry_summary);
  const nextSteps = asObject(resultRoot?.next_steps);
  const meta = asObject(resultRoot?.meta);

  const patterns = [
    ...toStringArray(pantrySummary?.nutrition_flags),
    ...toStringArray(pantrySummary?.patterns_noted),
    ...toStringArray(pantrySummary?.patterns),
    ...toStringArray(resultRoot?.nutrition_flags),
  ];

  const gaps = [
    ...toStringArray(pantrySummary?.gaps_to_consider),
    ...toStringArray(pantrySummary?.gaps),
    ...toStringArray(resultRoot?.gaps_to_consider),
    ...toStringArray(resultRoot?.gaps),
  ];

  const shoppingList = [
    ...toStringArray(nextSteps?.shopping_list),
    ...toStringArray(nextSteps?.top_5_shopping_list),
    ...toStringArray(resultRoot?.shopping_list),
  ];
  const quickWins = [
    ...toStringArray(nextSteps?.quick_wins),
    ...toStringArray(nextSteps?.habit_changes),
    ...toStringArray(pantrySummary?.quick_wins),
  ];

  return {
    status: pickFirstString(root, ["status"]) ?? pickFirstString(resultRoot, ["status"]),
    overallAssessment:
      pickFirstString(pantrySummary, ["overall_assessment", "overallAssessment", "summary"]) ??
      pickFirstString(resultRoot, ["overall_assessment", "overallAssessment", "summary", "short_summary"]),
    patterns,
    gaps,
    shoppingList,
    quickWins,
    assumptions: toStringArray(meta?.assumptions),
    alternatives: extractAlternatives(resultRoot?.item_alternatives),
    generatedAt: pickFirstString(meta, ["generated_at", "generatedAt"]),
    version: pickFirstString(meta, ["version", "model", "schema"]),
  };
}

function statusLabel(value?: string | null): string {
  const source = (value || "").trim();
  if (!source) {
    return "UNKNOWN";
  }
  return source.replace(/_/g, " ").toUpperCase();
}

const SWAP_SWIPE_THRESHOLD = 90;
const SWAP_SWIPE_LIMIT = 220;

function clampSwipeOffset(value: number): number {
  return Math.max(-SWAP_SWIPE_LIMIT, Math.min(SWAP_SWIPE_LIMIT, value));
}

function SmartSwapsDeck({ alternatives }: { alternatives: InsightAlternative[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef<number | null>(null);
  const dragPointerRef = useRef<number | null>(null);

  const total = alternatives.length;
  const hasPrevious = activeIndex > 0;
  const hasNext = activeIndex < total - 1;
  const activeAlternative = alternatives[activeIndex] ?? null;
  const visibleAlternatives = alternatives.slice(activeIndex, activeIndex + 3);

  useEffect(() => {
    setActiveIndex(0);
    setDragOffset(0);
    setIsDragging(false);
    dragStartXRef.current = null;
    dragPointerRef.current = null;
  }, [total]);

  function goTo(index: number): void {
    setActiveIndex(Math.max(0, Math.min(total - 1, index)));
    setDragOffset(0);
    setIsDragging(false);
  }

  function onPointerDown(event: React.PointerEvent<HTMLElement>): void {
    if (total <= 1) {
      return;
    }
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }
    dragStartXRef.current = event.clientX;
    dragPointerRef.current = event.pointerId;
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: React.PointerEvent<HTMLElement>): void {
    if (!isDragging || dragStartXRef.current === null || dragPointerRef.current !== event.pointerId) {
      return;
    }
    const deltaX = clampSwipeOffset(event.clientX - dragStartXRef.current);
    setDragOffset(deltaX);
  }

  function onPointerEnd(event: React.PointerEvent<HTMLElement>): void {
    if (dragPointerRef.current !== event.pointerId) {
      return;
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const startX = dragStartXRef.current;
    const deltaX = startX === null ? 0 : clampSwipeOffset(event.clientX - startX);
    dragStartXRef.current = null;
    dragPointerRef.current = null;
    setIsDragging(false);
    setDragOffset(0);

    if (deltaX <= -SWAP_SWIPE_THRESHOLD && hasNext) {
      setActiveIndex((value) => Math.min(total - 1, value + 1));
      return;
    }
    if (deltaX >= SWAP_SWIPE_THRESHOLD && hasPrevious) {
      setActiveIndex((value) => Math.max(0, value - 1));
    }
  }

  if (!activeAlternative) {
    return null;
  }

  const progress = total <= 1 ? 100 : Math.round(((activeIndex + 1) / total) * 100);

  return (
    <div className="mt-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">
          Swipe card {activeIndex + 1} of {total}
        </p>
        <div className="h-2 w-32 overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--pr-surface-secondary)_80%,transparent)]">
          <div
            className="h-full rounded-full bg-[var(--pr-accent-gradient)] transition-[width] duration-200 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="pr-swap-deck">
        <div className="pr-swap-stack-glow" aria-hidden />
        {visibleAlternatives
          .slice()
          .reverse()
          .map((alternative, reverseDepth) => {
            const depth = visibleAlternatives.length - reverseDepth - 1;
            const isTop = depth === 0;
            const cardY = depth * 12;
            const cardScale = 1 - depth * 0.035;
            const opacity = 1 - depth * 0.17;
            const translateX = isTop ? dragOffset : 0;
            const rotate = isTop ? dragOffset / 26 : 0;
            return (
              <article
                key={alternative.id}
                className={`pr-swap-card ${isTop ? "pr-swap-card-active" : "pr-swap-card-rest"}`}
                tabIndex={isTop ? 0 : -1}
                onPointerDown={isTop ? onPointerDown : undefined}
                onPointerMove={isTop ? onPointerMove : undefined}
                onPointerUp={isTop ? onPointerEnd : undefined}
                onPointerCancel={isTop ? onPointerEnd : undefined}
                onKeyDown={
                  isTop
                    ? (event) => {
                        if (event.key === "ArrowLeft" && hasPrevious) {
                          event.preventDefault();
                          goTo(activeIndex - 1);
                        }
                        if (event.key === "ArrowRight" && hasNext) {
                          event.preventDefault();
                          goTo(activeIndex + 1);
                        }
                      }
                    : undefined
                }
                style={{
                  zIndex: 20 - depth,
                  opacity,
                  transform: `translate3d(${translateX}px, ${cardY}px, 0) scale(${cardScale}) rotate(${rotate}deg)`,
                  transition: isDragging && isTop ? "none" : "transform 220ms ease, opacity 220ms ease",
                }}
                aria-label={`Swap suggestion ${activeIndex + depth + 1}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Smart swap</p>
                    <p className="pr-display text-xl font-semibold text-[var(--pr-text)]">
                      {alternative.current} {"->"} {alternative.swap}
                    </p>
                  </div>
                  {alternative.tier ? <Pill tone="accent" label={alternative.tier} /> : null}
                </div>

                <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                  <div className="pr-swap-chip">
                    <p className="pr-swap-chip-label">Current</p>
                    <p className="text-sm font-semibold">{alternative.current}</p>
                  </div>
                  <p className="hidden text-center text-2xl font-semibold text-[var(--pr-accent-deep)]">{"->"}</p>
                  <div className="pr-swap-chip pr-swap-chip-target">
                    <p className="pr-swap-chip-label">Better swap</p>
                    <p className="text-sm font-semibold">{alternative.swap}</p>
                  </div>
                </div>

                <div className="mt-4 rounded-xl border border-[var(--pr-border)] bg-[color-mix(in_srgb,var(--pr-surface)_75%,transparent)] p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Why this helps</p>
                  <p className="mt-1 text-sm text-[var(--pr-text-muted)]">
                    {alternative.reason || "A simpler swap that may improve the overall variety in your pantry."}
                  </p>
                </div>

                <p className="mt-3 text-xs text-[var(--pr-text-muted)]">
                  Swipe left for next card, swipe right for previous.
                </p>
              </article>
            );
          })}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button className="min-h-10 px-4 py-2" variant="secondary" onClick={() => goTo(activeIndex - 1)} disabled={!hasPrevious}>
          Previous
        </Button>
        <p className="text-sm font-semibold text-[var(--pr-text-muted)]">
          {activeIndex + 1} / {total}
        </p>
        <Button className="min-h-10 px-4 py-2" variant="secondary" onClick={() => goTo(activeIndex + 1)} disabled={!hasNext}>
          Next
        </Button>
      </div>
    </div>
  );
}

export function AIInsightDetailPage() {
  const { jobId = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const [pendingRefresh, setPendingRefresh] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<AIInsightRequest | null>(null);
  const [result, setResult] = useState<unknown>(null);
  const insight = useMemo(() => extractInsightView(result), [result]);

  async function load(withSpinner = true): Promise<void> {
    if (!jobId) {
      return;
    }

    if (withSpinner) {
      setLoading(true);
    }
    setError(null);

    try {
      const [statusPayload, resultPayload] = await Promise.all([fetchAiInsightStatus(jobId), fetchAiInsightResult(jobId)]);
      setStatus(statusPayload);
      setResult(resultPayload);
      if (withSpinner) {
        trackEvent("insight_detail_viewed", { job_id: jobId, status: statusPayload?.status });
      } else {
        trackEvent("insight_detail_refreshed", { job_id: jobId, status: statusPayload?.status });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load insight detail.");
      setStatus(null);
      setResult(null);
    } finally {
      setLoading(false);
      setPendingRefresh(false);
    }
  }

  useEffect(() => {
    void load();
  }, [jobId]);

  useEffect(() => {
    if (!status) {
      return;
    }
    const state = status.status.toLowerCase();
    if (state !== "queued" && state !== "running" && state !== "in_progress" && state !== "pending") {
      return;
    }

    const id = window.setInterval(() => {
      void load(false);
    }, 7000);

    return () => {
      window.clearInterval(id);
    };
  }, [status]);

  return (
    <div data-testid="ai-insight-detail-page" className="space-y-4 pb-4">
      <PageHeader
        title="AI insight detail"
        subtitle="Full analysis breakdown and suggestions."
        right={
          <Link to="/app/ai-insights" className="text-sm font-semibold text-[var(--pr-accent-deep)]">
            Back to insights
          </Link>
        }
      />

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-72" />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => void load()} />
      ) : (
        <>
          <Card>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">Analysis run</h2>
                <p className="mt-1 text-sm text-[var(--pr-text-muted)]">Created {displayDate(status?.created_at)}</p>
                <p className="text-sm text-[var(--pr-text-muted)]">Updated {displayDate(status?.updated_at)}</p>
              </div>
              <Pill tone="accent" label={statusLabel(status?.status ?? insight.status)} />
            </div>

            <div className="mt-4 flex gap-2">
              <Button
                variant="secondary"
                disabled={pendingRefresh}
                onClick={() => {
                  setPendingRefresh(true);
                  void load(false);
                }}
              >
                {pendingRefresh ? "Refreshing..." : "Refresh status"}
              </Button>
            </div>
          </Card>

          <Card data-testid="ai-insight-overview-card">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Overall assessment</p>
            <p className="mt-2 text-base leading-relaxed">
              {insight.overallAssessment || status?.summary || "PantryRx is preparing a summary for this insight job."}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <Card flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Patterns</p>
                <p className="pr-display mt-2 text-3xl font-semibold">{insight.patterns.length}</p>
              </Card>
              <Card flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Priority gaps</p>
                <p className="pr-display mt-2 text-3xl font-semibold">{insight.gaps.length}</p>
              </Card>
              <Card flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Action steps</p>
                <p className="pr-display mt-2 text-3xl font-semibold">{insight.shoppingList.length + insight.quickWins.length}</p>
              </Card>
            </div>
          </Card>

          <div className="grid gap-3 lg:grid-cols-2">
            <Card>
              <h3 className="text-base font-semibold">Patterns to maintain</h3>
              {insight.patterns.length === 0 ? (
                <p className="mt-2 text-sm text-[var(--pr-text-muted)]">No pattern highlights were returned for this run.</p>
              ) : (
                <ul className="mt-3 space-y-2 text-sm leading-relaxed">
                  {insight.patterns.map((pattern) => (
                    <li key={pattern} className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
                      {pattern}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card>
              <h3 className="text-base font-semibold">Gaps to improve</h3>
              {insight.gaps.length === 0 ? (
                <p className="mt-2 text-sm text-[var(--pr-text-muted)]">No explicit nutrition gaps were flagged yet.</p>
              ) : (
                <ul className="mt-3 space-y-2 text-sm leading-relaxed">
                  {insight.gaps.map((gap) => (
                    <li key={gap} className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
                      {gap}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <Card>
            <h3 className="text-base font-semibold">Next steps this week</h3>
            {insight.shoppingList.length === 0 && insight.quickWins.length === 0 ? (
              <EmptyState title="No suggestions yet" subtitle="Refresh the job after analysis completes to see personalized next steps." />
            ) : (
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                <Card flat>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Shopping list</p>
                  {insight.shoppingList.length === 0 ? (
                    <p className="mt-2 text-sm text-[var(--pr-text-muted)]">No shopping suggestions in this response.</p>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {insight.shoppingList.map((entry) => (
                        <Pill key={entry} label={entry} />
                      ))}
                    </div>
                  )}
                </Card>
                <Card flat>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Quick wins</p>
                  {insight.quickWins.length === 0 ? (
                    <p className="mt-2 text-sm text-[var(--pr-text-muted)]">No quick-win suggestions in this response.</p>
                  ) : (
                    <ul className="mt-2 space-y-2 text-sm leading-relaxed">
                      {insight.quickWins.map((entry) => (
                        <li key={entry} className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
                          {entry}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            )}
          </Card>

          {insight.alternatives.length > 0 ? (
            <Card>
              <h3 className="text-base font-semibold">Smart swaps</h3>
              <SmartSwapsDeck alternatives={insight.alternatives} />
            </Card>
          ) : null}

          <Card>
            <h3 className="text-base font-semibold">Analysis context</h3>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <Card flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Generated</p>
                <p className="mt-1 text-sm font-semibold">{displayDate(insight.generatedAt ?? undefined)}</p>
              </Card>
              <Card flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Model profile</p>
                <p className="mt-1 text-sm font-semibold">{insight.version || "Not provided"}</p>
              </Card>
            </div>
            {insight.assumptions.length > 0 ? (
              <ul className="mt-3 space-y-2 text-sm text-[var(--pr-text-muted)]">
                {insight.assumptions.map((assumption) => (
                  <li key={assumption} className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
                    {assumption}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-[var(--pr-text-muted)]">No additional assumptions were included in this response.</p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
